
import { encryptedOAuthCache } from './oauth-cache-encrypted';

interface CacheItem<T> {
  data: T;
  timestamp: number;
  expiresAt: number;
  accessCount: number;
  lastAccessed: number;
}

interface LRUNode<T> {
  key: string;
  value: CacheItem<T>;
  prev?: LRUNode<T>;
  next?: LRUNode<T>;
}

class LRUCache<T> {
  private capacity: number;
  private cache: Map<string, LRUNode<T>>;
  private head: LRUNode<T>;
  private tail: LRUNode<T>;

  constructor(capacity: number = 50) {
    this.capacity = capacity;
    this.cache = new Map();
    
    // Create dummy head and tail nodes
    this.head = { key: '', value: {} as CacheItem<T> };
    this.tail = { key: '', value: {} as CacheItem<T> };
    this.head.next = this.tail;
    this.tail.prev = this.head;
  }

  private addToHead(node: LRUNode<T>): void {
    node.prev = this.head;
    node.next = this.head.next;
    this.head.next!.prev = node;
    this.head.next = node;
  }

  private removeNode(node: LRUNode<T>): void {
    node.prev!.next = node.next;
    node.next!.prev = node.prev;
  }

  private moveToHead(node: LRUNode<T>): void {
    this.removeNode(node);
    this.addToHead(node);
  }

  private removeTail(): LRUNode<T> {
    const lastNode = this.tail.prev!;
    this.removeNode(lastNode);
    return lastNode;
  }

  get(key: string): T | null {
    const node = this.cache.get(key);
    if (!node) return null;

    // Check expiration
    const now = Date.now();
    if (now > node.value.expiresAt) {
      this.cache.delete(key);
      this.removeNode(node);
      return null;
    }

    // Update access info
    node.value.lastAccessed = now;
    node.value.accessCount++;

    // Move to head (most recently used)
    this.moveToHead(node);

    return node.value.data;
  }

  set(key: string, value: T, ttlMs: number): void {
    const now = Date.now();
    const expiresAt = now + ttlMs;

    const existingNode = this.cache.get(key);
    if (existingNode) {
      // Update existing
      existingNode.value.data = value;
      existingNode.value.timestamp = now;
      existingNode.value.expiresAt = expiresAt;
      existingNode.value.lastAccessed = now;
      existingNode.value.accessCount++;
      this.moveToHead(existingNode);
      return;
    }

    // Create new node
    const newNode: LRUNode<T> = {
      key,
      value: {
        data: value,
        timestamp: now,
        expiresAt,
        accessCount: 1,
        lastAccessed: now
      }
    };

    // Add to cache
    this.cache.set(key, newNode);
    this.addToHead(newNode);

    // Check capacity
    if (this.cache.size > this.capacity) {
      const tail = this.removeTail();
      this.cache.delete(tail.key);
    }
  }

  delete(key: string): boolean {
    const node = this.cache.get(key);
    if (!node) return false;

    this.cache.delete(key);
    this.removeNode(node);
    return true;
  }

  clear(): void {
    this.cache.clear();
    this.head.next = this.tail;
    this.tail.prev = this.head;
  }

  size(): number {
    return this.cache.size;
  }

  getStats() {
    const now = Date.now();
    let validItems = 0;
    let expiredItems = 0;
    let totalAccessCount = 0;

    for (const [key, node] of this.cache) {
      if (now > node.value.expiresAt) {
        expiredItems++;
      } else {
        validItems++;
      }
      totalAccessCount += node.value.accessCount;
    }

    return {
      capacity: this.capacity,
      size: this.cache.size,
      validItems,
      expiredItems,
      averageAccess: validItems > 0 ? totalAccessCount / validItems : 0
    };
  }

  cleanup(): number {
    const now = Date.now();
    const expiredKeys: string[] = [];

    for (const [key, node] of this.cache) {
      if (now > node.value.expiresAt) {
        expiredKeys.push(key);
      }
    }

    expiredKeys.forEach(key => {
      const node = this.cache.get(key);
      if (node) {
        this.cache.delete(key);
        this.removeNode(node);
      }
    });

    return expiredKeys.length;
  }
}

export const memoryOAuthCache = {
  // Cache instances
  tradingAccountsCache: new LRUCache<any[]>(20),
  adminCredentialsCache: new LRUCache<any>(5),
  tokensCache: new LRUCache<any>(10),

  // Trading accounts with memory-first approach and proper error handling
  setTradingAccounts: (accounts: any[], cspAccountId: string) => {
    const cacheKey = `trading_accounts_${cspAccountId}`;
    const ttl = 5 * 60 * 1000; // 5 minutes

    try {
      // Store in memory cache
      memoryOAuthCache.tradingAccountsCache.set(cacheKey, accounts, ttl);

      // Fallback to encrypted localStorage with proper error handling
      encryptedOAuthCache.setTradingAccounts(accounts, cspAccountId);
    } catch (e) {
      console.warn('Failed to cache trading accounts:', e);
    }
  },

  getTradingAccounts: (cspAccountId: string): any[] | null => {
    const cacheKey = `trading_accounts_${cspAccountId}`;

    try {
      // Try memory cache first
      const memoryResult = memoryOAuthCache.tradingAccountsCache.get(cacheKey);
      if (memoryResult) {
        return memoryResult;
      }

      // Fallback to encrypted localStorage
      const result = encryptedOAuthCache.getTradingAccounts(cspAccountId);
      
      // Populate memory cache from localStorage
      if (result) {
        memoryOAuthCache.tradingAccountsCache.set(cacheKey, result, 5 * 60 * 1000);
      }
      
      return result;
    } catch (e) {
      console.warn('Failed to retrieve trading accounts from cache:', e);
      return null;
    }
  },

  // Admin credentials with memory-first approach and proper error handling
  setAdminCredentials: (credentials: any) => {
    const cacheKey = 'admin_credentials';
    const ttl = 30 * 60 * 1000; // 30 minutes

    try {
      // Store in memory cache
      memoryOAuthCache.adminCredentialsCache.set(cacheKey, credentials, ttl);

      // Fallback to encrypted localStorage
      encryptedOAuthCache.setAdminCredentials(credentials);
    } catch (e) {
      console.warn('Failed to cache admin credentials:', e);
    }
  },

  getAdminCredentials: (): any | null => {
    const cacheKey = 'admin_credentials';

    try {
      // Try memory cache first
      const memoryResult = memoryOAuthCache.adminCredentialsCache.get(cacheKey);
      if (memoryResult) {
        return memoryResult;
      }

      // Fallback to encrypted localStorage
      const result = encryptedOAuthCache.getAdminCredentials();
      
      // Populate memory cache from localStorage
      if (result) {
        memoryOAuthCache.adminCredentialsCache.set(cacheKey, result, 30 * 60 * 1000);
      }
      
      return result;
    } catch (e) {
      console.warn('Failed to retrieve admin credentials from cache:', e);
      return null;
    }
  },

  // Cache management with error handling
  clearAll: () => {
    try {
      memoryOAuthCache.tradingAccountsCache.clear();
      memoryOAuthCache.adminCredentialsCache.clear();
      memoryOAuthCache.tokensCache.clear();

      // Also clear localStorage fallback
      encryptedOAuthCache.clearAll();
    } catch (e) {
      console.warn('Failed to clear cache:', e);
    }
  },

  clearTradingAccounts: (cspAccountId: string) => {
    try {
      const cacheKey = `trading_accounts_${cspAccountId}`;
      memoryOAuthCache.tradingAccountsCache.delete(cacheKey);

      // Also clear from localStorage
      encryptedOAuthCache.clearTradingAccounts(cspAccountId);
    } catch (e) {
      console.warn('Failed to clear trading accounts cache:', e);
    }
  },

  // Maintenance and stats with error handling
  performMaintenance: () => {
    try {
      const stats = {
        tradingAccounts: memoryOAuthCache.tradingAccountsCache.cleanup(),
        adminCredentials: memoryOAuthCache.adminCredentialsCache.cleanup(),
        tokens: memoryOAuthCache.tokensCache.cleanup()
      };

      // Also perform localStorage maintenance
      encryptedOAuthCache.performMaintenance();

      return stats;
    } catch (e) {
      console.warn('Failed to perform cache maintenance:', e);
      return {
        tradingAccounts: 0,
        adminCredentials: 0,
        tokens: 0
      };
    }
  },

  getStats: () => {
    try {
      return {
        tradingAccounts: memoryOAuthCache.tradingAccountsCache.getStats(),
        adminCredentials: memoryOAuthCache.adminCredentialsCache.getStats(),
        tokens: memoryOAuthCache.tokensCache.getStats()
      };
    } catch (e) {
      console.warn('Failed to get cache stats:', e);
      return {
        tradingAccounts: { capacity: 0, size: 0, validItems: 0, expiredItems: 0, averageAccess: 0 },
        adminCredentials: { capacity: 0, size: 0, validItems: 0, expiredItems: 0, averageAccess: 0 },
        tokens: { capacity: 0, size: 0, validItems: 0, expiredItems: 0, averageAccess: 0 }
      };
    }
  }
};
